import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useRecoilState } from "recoil";
import { noteExpiryAtom } from "../../atoms/expiry.atom";
import {
  editorModeAtom,
  saveModeAtom,
  viewRecentModeAtom,
} from "../../atoms/modes.atom";
import { noteTitle, isLiveMode } from "../../atoms/note.atom";
import InfoMessageComponent from "../commonComponent/InfoMessageComponent";
import Header from "../molecules/Header";
import NoteActionBar from "../molecules/NoteActionBar";
import CanvasFullscreenBar from "../molecules/CanvasFullscreenBar";
import RecentTitlesDrawer from "../molecules/RecentTitlesDrawer";
import RecentTitlesPanel from "../molecules/RecentTitlesPanel";
import CanvasWorkspace from "../organisms/CanvasWorkspace";
import NoteEditorWorkspace from "../organisms/NoteEditorWorkspace";
import {
  DeleteNoteByTitle,
  RetrieveNoteByName,
  StoreNoteDataObject,
  getAllStoredNoteTitles,
} from "../../repository/note.repository";
import {
  deleteNote,
  getNote,
  updateCloudNote,
} from "../../api/notes.api";
import {
  MAX_NOTE_ATTACHMENT_BYTES,
  MAX_NOTE_ATTACHMENT_COUNT,
} from "../../constants/noteAttachment.constants";
import { EDITOR_MODES } from "../../constants/editorMode.constants";
import {
  getAttachmentFileKey,
  readNoteAttachments,
  validateNoteAttachmentFile,
} from "../../utils/noteAttachment.util";
import { createEmptyCanvasContent } from "../../features/canvas/canvasContent";
import { CANVAS_AI_DRAFT_DOCUMENT_KEY } from "../../features/canvasAi/canvasAi.constants";
import {
  clearCanvasAiConversation,
  migrateCanvasAiConversation,
} from "../../features/canvasAi/canvasAi.storage";
import {
  STORED_NOTE_FORMATS,
  parseStoredNoteContent,
  serializeStoredNoteContent,
} from "../../features/noteDocument/noteDocument";
import NearbyNetpadsPanel from "../../features/nearbyTransfer/components/NearbyNetpadsPanel";
import { useNearbyTransferSession } from "../../features/nearbyTransfer/hooks/useNearbyTransferSession";
import { isNearbyTransferBetaCode } from "../../features/nearbyTransfer/utils/featureFlag";
import { FILE_TRANSFER_PROMO_PHASES } from "../../features/fileTransferPromo/fileTransferPromo.constants";
import { hasSeenFileTransferPromo } from "../../features/fileTransferPromo/fileTransferPromo.storage";
import {
  isDesktopFileTransferPromoViewport,
  isFileTransferPromoEligible,
  isFileTransferPromoRouteAllowed,
} from "../../features/fileTransferPromo/fileTransferPromo.rules";
import { useFileTransferPromo } from "../../features/fileTransferPromo/useFileTransferPromo";
import { useAutoSave } from "../../features/autoSave/useAutoSave";
import {
  readAutoSavePreference,
  storeAutoSavePreference,
} from "../../features/autoSave/autoSavePreference";

import SplashScreenAnimation from "../atoms/SplashScreen/SplashScreen";

const COPYRIGHT_START_YEAR = 2019;
const RECENT_TITLES_STORAGE_KEY = "recentTitles";
const SPLASH_LAST_SHOWN_STORAGE_KEY = "splashLastShownAt";
const MESSAGE_TIMEOUT_MS = 2000;
const FIRST_VISIT_DELAY_MS = 1500;
const SPLASH_REPEAT_INTERVAL_HOURS = 50;
const MS_PER_HOUR = 60 * 60 * 1000;
const SPLASH_REPEAT_INTERVAL_MS = SPLASH_REPEAT_INTERVAL_HOURS * MS_PER_HOUR;
const ATTACHMENT_SIZE_ERROR_MESSAGE = "File must be 10 MB or smaller";
const ATTACHMENT_COUNT_ERROR_MESSAGE = `You can attach up to ${MAX_NOTE_ATTACHMENT_COUNT} files`;
const ATTACHMENT_DOWNLOAD_ERROR_MESSAGE = "Could not download file";
const DEFAULT_ATTACHMENT_FILE_NAME = "attachment";
const OBJECT_URL_REVOKE_DELAY_MS = 1000;
const NEARBY_BETA_ENABLED_MESSAGE = "Nearby Netpads beta enabled";
const ONLINE_NOTE_SAVED_MESSAGE = "Note Saved";
const OFFLINE_NOTE_SAVED_MESSAGE = "Note Saved Locally";
const NOTE_SAVE_ERROR_MESSAGE = "Could not save note";
const NO_TITLE_MESSAGE = "No Title";
const LOCKED_BODY_OVERFLOW = "hidden";
const AUTO_SAVE_EMPTY_SNAPSHOT_VALUE = "";
const AUTO_SAVE_OPTIONS = {
  shouldSetLoading: false,
  shouldSaveAttachmentRemoval: false,
  shouldShowErrorMessage: false,
  shouldShowSuccessMessage: false,
};

const normalizeOnlineTitle = (title) => String(title).toLowerCase().trim();

const buildCanvasAiDocumentKey = ({ mode, title }) => {
  const cleanTitle = String(title).trim();
  if (!cleanTitle) return CANVAS_AI_DRAFT_DOCUMENT_KEY;
  const storedTitle = mode === "online" ? normalizeOnlineTitle(cleanTitle) : cleanTitle;
  return `${mode}:${storedTitle}`;
};

const buildAutoSaveSnapshot = ({ canvasContent, content, subject }) =>
  JSON.stringify([
    subject ?? AUTO_SAVE_EMPTY_SNAPSHOT_VALUE,
    content ?? AUTO_SAVE_EMPTY_SNAPSHOT_VALUE,
    canvasContent ?? AUTO_SAVE_EMPTY_SNAPSHOT_VALUE,
  ]);

const buildSaveOptions = ({
  shouldSaveAttachmentRemoval = true,
  shouldSetLoading = true,
  shouldShowErrorMessage = true,
  shouldShowSuccessMessage = true,
} = {}) => ({
  shouldSaveAttachmentRemoval,
  shouldSetLoading,
  shouldShowErrorMessage,
  shouldShowSuccessMessage,
});

const setSaveLoading = ({ isLoading, setLoading, shouldSetLoading }) => {
  if (!shouldSetLoading) return;

  setLoading(isLoading);
};

const shouldShowSplashScreen = ({ lastShownAt, currentTime }) => {
  if (!lastShownAt) return true;

  const lastShownTime = Number(lastShownAt);
  if (Number.isNaN(lastShownTime)) return true;

  return currentTime - lastShownTime > SPLASH_REPEAT_INTERVAL_MS;
};

const shouldSkipSplashForFileTransferPromo = ({
  isAllowedRoute,
  noteContent,
  noteTitle,
}) => {
  const hasSeenPromo = hasSeenFileTransferPromo();
  const isDesktop = isDesktopFileTransferPromoViewport();

  return isFileTransferPromoEligible({
    hasAllowedRoute: isAllowedRoute,
    hasSeenPromo,
    isDesktop,
    noteContent,
    noteTitle,
  });
};

const isExpiredAttachmentUrl = (attachment) => {
  if (!attachment?.expiresAt) return false;

  return new Date(attachment.expiresAt).getTime() <= Date.now();
};

const fetchAttachmentBlob = async (attachment) => {
  const response = await fetch(attachment.downloadUrl);
  if (!response.ok) {
    throw new Error(`Attachment download failed with status ${response.status}`);
  }

  return response.blob();
};

const triggerBlobDownload = ({ blob, fileName }) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = fileName || DEFAULT_ATTACHMENT_FILE_NAME;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), OBJECT_URL_REVOKE_DELAY_MS);
};

const downloadAttachmentFile = async (attachment) => {
  const blob = await fetchAttachmentBlob(attachment);
  triggerBlobDownload({
    blob,
    fileName: attachment.fileName,
  });
};

const HomeScreen = () => {
  const [typeOfMessage, setTypeOfMessage] = useState("");
  const [message, setMessage] = useState("");
  const [content, setContent] = useState("");
  const [canvasContent, setCanvasContent] = useState(createEmptyCanvasContent);
  const [canvasDocumentKey, setCanvasDocumentKey] = useState(
    CANVAS_AI_DRAFT_DOCUMENT_KEY
  );
  const [subject, setSubject] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState([]);
  const [savedAttachments, setSavedAttachments] = useState([]);
  const [pendingRemovalBlobNames, setPendingRemovalBlobNames] = useState([]);
  const [recentNotes, setRecentNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSplashVisible, setIsSplashVisible] = useState(false);
  const [isNearbyTransferEnabled, setIsNearbyTransferEnabled] = useState(false);
  const [isAutoSaveEnabled, setIsAutoSaveEnabled] = useState(
    readAutoSavePreference
  );

  const [noteExpiry, setNoteExpiry] = useRecoilState(noteExpiryAtom);
  const [recentViewNoteMode, setRecentViewNoteMode] =
    useRecoilState(viewRecentModeAtom);
  const [saveMode, setSaveMode] = useRecoilState(saveModeAtom);
  const [editorMode, setEditorMode] = useRecoilState(editorModeAtom);
  const [, setTitle] = useRecoilState(noteTitle);
  const [isRecentDrawerOpen, setRecentDrawerOpen] = useState(false);
  const [isCanvasFullscreen, setIsCanvasFullscreen] = useState(false);
  const [, setIsLive] = useRecoilState(isLiveMode);
  const canvasWorkspaceRef = useRef(null);
  const saveButtonHandlerRef = useRef(null);
  const inFlightSaveRef = useRef(null);
  const isCanvasMode = editorMode === EDITOR_MODES.CANVAS;
  const activeContent = isCanvasMode ? canvasContent : content;
  // Canvas content reaches `canvasContent` through a debounced onChange, so
  // explicit saves read the live scene straight from the workspace while it
  // is mounted; in note mode the unmount flush already synced the state.
  const resolveCanvasContentToSave = () =>
    isCanvasMode
      ? canvasWorkspaceRef.current?.getContent() ?? canvasContent
      : canvasContent;
  // A title owns one stored payload carrying both editors: saves merge the
  // note text and canvas scene instead of dropping the inactive side.
  const resolveContentToSave = () =>
    serializeStoredNoteContent({
      canvasContent: resolveCanvasContentToSave(),
      noteText: content,
    });
  // Canvas images upload to blob storage only for titled cloud notes; `#`
  // (offline) notes keep data URLs in IndexedDB and never touch the server.
  const isCanvasCloudSyncEnabled =
    saveMode === "online" && Boolean(String(subject).trim());

  const dropdownOptions = [
    { label: "Never", value: "never" },
    { label: "5m", value: "5m" },
    { label: "1h", value: "1h" },
    { label: "24h", value: "24h" },
  ];
  const isFileTransferPromoAllowed = useMemo(
    () => isFileTransferPromoRouteAllowed(),
    []
  );
  const [shouldSkipInitialSplashForPromo] = useState(() =>
    shouldSkipSplashForFileTransferPromo({
      isAllowedRoute: isFileTransferPromoAllowed,
      noteContent: content,
      noteTitle: subject,
    })
  );
  const fileTransferPromo = useFileTransferPromo({
    isAllowedRoute: isFileTransferPromoAllowed,
    noteContent: content,
    noteTitle: subject,
  });
  const isFileTransferAttachmentHighlighted =
    fileTransferPromo.phase === FILE_TRANSFER_PROMO_PHASES.HOLDING;

  const nearbyTransferPayload = useMemo(
    () => ({
      title: subject,
      content: activeContent,
      // The nearby transfer protocol carries a single file.
      file: selectedAttachments[0] ?? null,
    }),
    [activeContent, selectedAttachments, subject]
  );
  const nearbyTransferSession = useNearbyTransferSession({
    isEnabled: isNearbyTransferEnabled,
    transferPayload: nearbyTransferPayload,
  });
  const autoSave = useAutoSave({
    canSave: Boolean(String(subject).trim()),
    getSnapshot: () =>
      buildAutoSaveSnapshot({ canvasContent, content, subject }),
    isEnabled: isAutoSaveEnabled,
    onSave: () => saveButtonHandler(AUTO_SAVE_OPTIONS),
  });

  const clearAttachmentState = () => {
    setSelectedAttachments([]);
    setSavedAttachments([]);
    setPendingRemovalBlobNames([]);
  };

  const promoteCanvasDocumentKey = ({ mode, title }) => {
    const nextDocumentKey = buildCanvasAiDocumentKey({ mode, title });
    if (canvasDocumentKey === CANVAS_AI_DRAFT_DOCUMENT_KEY) {
      migrateCanvasAiConversation({
        fromDocumentKey: CANVAS_AI_DRAFT_DOCUMENT_KEY,
        toDocumentKey: nextDocumentKey,
      });
    }
    setCanvasDocumentKey(nextDocumentKey);
  };

  const resetCanvasAiDocument = () => {
    clearCanvasAiConversation(canvasDocumentKey);
    clearCanvasAiConversation(CANVAS_AI_DRAFT_DOCUMENT_KEY);
    setCanvasDocumentKey(CANVAS_AI_DRAFT_DOCUMENT_KEY);
  };

  const storeRecentOnlineTitle = (noteTitle) => {
    const storedRecentTitles =
      JSON.parse(localStorage.getItem(RECENT_TITLES_STORAGE_KEY)) || [];
    const updatedRecentTitles = Array.from(
      new Set([...storedRecentTitles, noteTitle])
    ).slice(-10);
    localStorage.setItem(
      RECENT_TITLES_STORAGE_KEY,
      JSON.stringify(updatedRecentTitles)
    );
    setRecentNotes(updatedRecentTitles);
  };

  // Split decide/commit so the StrictMode double-run of the decide effect
  // never reads back the timestamp it wrote (which would strand the splash).
  useEffect(() => {
    if (shouldSkipInitialSplashForPromo) return;

    const lastShownAt = localStorage.getItem(SPLASH_LAST_SHOWN_STORAGE_KEY);
    if (!shouldShowSplashScreen({ lastShownAt, currentTime: Date.now() })) return;

    setIsSplashVisible(true);
  }, [shouldSkipInitialSplashForPromo]);

  useEffect(() => {
    if (!isSplashVisible) return undefined;

    localStorage.setItem(SPLASH_LAST_SHOWN_STORAGE_KEY, String(Date.now()));
    const timerId = setTimeout(() => {
      setIsSplashVisible(false);
    }, FIRST_VISIT_DELAY_MS);

    return () => clearTimeout(timerId);
  }, [isSplashVisible]);

  useEffect(() => {
    if (subject.startsWith("#")) {
      setSaveMode("offline");
    } else {
      setSaveMode("online");
    }
  }, [subject, setSaveMode]);

  useEffect(() => {
    let storedRecentTitles =
      JSON.parse(localStorage.getItem(RECENT_TITLES_STORAGE_KEY)) || [];
    setRecentNotes(storedRecentTitles);

    const fetchAndSetSubject = async () => {
      const url = new URL(window.location.href);
      const title = url.searchParams.get("title");
      if (title) {
        const normalizedTitle = normalizeOnlineTitle(title);
        if (!normalizedTitle) return;

        setSubject(normalizedTitle);
        setTitle(normalizedTitle);
        await viewButtonHandler(normalizedTitle);
      }
    };

    fetchAndSetSubject();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The listener is registered once; reading the handler through a ref keeps
  // every keystroke on the current render's state. A dependency-listed
  // closure here once went stale on editor-mode switches and saved empty
  // note content over freshly drawn canvases.
  useEffect(() => {
    const handleWindowSaveShortcut = (event) => {
      const isSaveCombo =
        (event.ctrlKey || event.metaKey) &&
        (event.key === "s" || event.key === "S");
      if (!isSaveCombo) return;

      event.preventDefault();
      saveButtonHandlerRef.current?.();
    };
    window.addEventListener("keydown", handleWindowSaveShortcut);
    return () => {
      window.removeEventListener("keydown", handleWindowSaveShortcut);
    };
  }, []);

  const showMessage = (type, message) => {
    setTypeOfMessage(type);
    setMessage(message);
    setTimeout(() => {
      setTypeOfMessage("");
      setMessage("");
    }, MESSAGE_TIMEOUT_MS);
  };

  const showConditionalSaveMessage = ({ canShow, message, type }) => {
    if (!canShow) return;

    showMessage(type, message);
  };

  const saveOfflineNote = async ({ content, saveOptions, subject }) => {
    setSaveMode("offline");
    await StoreNoteDataObject({ title: subject, content });
    const offlineNotesTitles = await getAllStoredNoteTitles();
    setRecentNotes(offlineNotesTitles);
    clearAttachmentState();
    promoteCanvasDocumentKey({ mode: "offline", title: subject });
    showConditionalSaveMessage({
      canShow: saveOptions.shouldShowSuccessMessage,
      message: OFFLINE_NOTE_SAVED_MESSAGE,
      type: "success",
    });
    return true;
  };

  // Re-reads the note after an upload so the panel shows server metadata for
  // every file. Only the files that were actually uploaded leave the
  // selection, and staged removals that autosave did not persist stay
  // staged: only blob names missing from the refreshed list are dropped.
  const refreshSavedAttachments = async ({ noteTitle, uploadedFiles }) => {
    const noteData = await getNote(noteTitle);
    const refreshedAttachments = readNoteAttachments(noteData);
    setSavedAttachments(refreshedAttachments);
    setSelectedAttachments((previousFiles) =>
      previousFiles.filter((file) => !uploadedFiles.includes(file))
    );
    setPendingRemovalBlobNames((previousBlobNames) =>
      previousBlobNames.filter((blobName) =>
        refreshedAttachments.some(
          (attachment) => attachment.blobName === blobName
        )
      )
    );
  };

  const completeOnlineAttachmentSave = async ({
    noteTitle,
    persistedRemovalBlobNames,
    uploadedFiles,
  }) => {
    if (uploadedFiles.length) {
      await refreshSavedAttachments({ noteTitle, uploadedFiles });
      return;
    }

    if (!persistedRemovalBlobNames.length) return;

    setSavedAttachments((previousAttachments) =>
      previousAttachments.filter(
        (attachment) => !persistedRemovalBlobNames.includes(attachment.blobName)
      )
    );
    setPendingRemovalBlobNames((previousBlobNames) =>
      previousBlobNames.filter(
        (blobName) => !persistedRemovalBlobNames.includes(blobName)
      )
    );
  };

  const saveOnlineNote = async ({ content, noteExpiry, saveOptions, subject }) => {
    const normalizedTitle = normalizeOnlineTitle(subject);
    const attachmentsToUpload = selectedAttachments;
    const persistedRemovalBlobNames = saveOptions.shouldSaveAttachmentRemoval
      ? pendingRemovalBlobNames
      : [];
    const updateNoteData = {
      title: normalizedTitle,
      content,
      expiry: noteExpiry,
    };
    const response = await updateCloudNote({
      attachments: attachmentsToUpload,
      noteData: updateNoteData,
      removeAttachmentBlobNames: persistedRemovalBlobNames,
    });

    if (!response.success) return false;

    storeRecentOnlineTitle(normalizedTitle);
    promoteCanvasDocumentKey({ mode: "online", title: normalizedTitle });
    await completeOnlineAttachmentSave({
      noteTitle: normalizedTitle,
      persistedRemovalBlobNames,
      uploadedFiles: attachmentsToUpload,
    });
    showConditionalSaveMessage({
      canShow: saveOptions.shouldShowSuccessMessage,
      message: ONLINE_NOTE_SAVED_MESSAGE,
      type: "success",
    });
    return true;
  };

  const showNoTitleSaveError = (saveOptions) => {
    showConditionalSaveMessage({
      canShow: saveOptions.shouldShowErrorMessage,
      message: NO_TITLE_MESSAGE,
      type: "error",
    });
    return false;
  };

  const showSaveFailure = ({ error, saveOptions }) => {
    console.error("Failed to save note:", error);
    showConditionalSaveMessage({
      canShow: saveOptions.shouldShowErrorMessage,
      message: NOTE_SAVE_ERROR_MESSAGE,
      type: "error",
    });
  };

  const saveCurrentNote = ({ content, noteExpiry, saveOptions, subject }) => {
    if (subject.startsWith("#")) {
      return saveOfflineNote({ content, saveOptions, subject });
    }

    return saveOnlineNote({ content, noteExpiry, saveOptions, subject });
  };

  const setCurrentSaveLoading = ({ isLoading, saveOptions }) => {
    setSaveLoading({
      isLoading,
      setLoading,
      shouldSetLoading: saveOptions.shouldSetLoading,
    });
  };

  const performNoteSave = async (saveOptions) => {
    setCurrentSaveLoading({ isLoading: true, saveOptions });
    try {
      // Flush pending canvas image uploads first so the saved content
      // carries slim blob refs instead of inline base64 pixels.
      if (isCanvasMode) await canvasWorkspaceRef.current?.waitForImageSync?.();
      const didSave = await saveCurrentNote({
        content: resolveContentToSave(),
        noteExpiry,
        saveOptions,
        subject,
      });
      if (didSave) autoSave.markSaved();

      return didSave;
    } catch (error) {
      showSaveFailure({ error, saveOptions });
      return false;
    } finally {
      setCurrentSaveLoading({ isLoading: false, saveOptions });
    }
  };

  // Save requests arrive from several surfaces at once (button, title-input
  // Cmd+S, canvas Cmd+S, autosave); concurrent calls join the in-flight save
  // so the note is written exactly once instead of racing PUTs.
  const saveButtonHandler = async (options = {}) => {
    if (inFlightSaveRef.current) return inFlightSaveRef.current;

    const saveOptions = buildSaveOptions(options);
    if (!String(subject).trim()) return showNoTitleSaveError(saveOptions);

    const savePromise = performNoteSave(saveOptions);
    inFlightSaveRef.current = savePromise;
    try {
      return await savePromise;
    } finally {
      inFlightSaveRef.current = null;
    }
  };

  useEffect(() => {
    saveButtonHandlerRef.current = saveButtonHandler;
  });

  // Loading resets both editors from the stored payload (a stale canvas or
  // note from the previous title must never merge into the loaded one). The
  // editor mode follows the stored format; combined documents keep the
  // current mode since both sides have content.
  const applyLoadedContent = (loadedContent) => {
    const loadedDocument = parseStoredNoteContent(loadedContent);
    setContent(loadedDocument.noteText);
    setCanvasContent(loadedDocument.canvasContent);
    if (loadedDocument.format === STORED_NOTE_FORMATS.CANVAS) {
      setEditorMode(EDITOR_MODES.CANVAS);
    } else if (loadedDocument.format === STORED_NOTE_FORMATS.TEXT) {
      setEditorMode(EDITOR_MODES.NOTE);
    }

    return loadedDocument;
  };

  const buildLoadedSnapshot = ({ loadedDocument, subject }) =>
    buildAutoSaveSnapshot({
      canvasContent: loadedDocument.canvasContent,
      content: loadedDocument.noteText,
      subject,
    });

  const applyFetchedOnlineNote = ({ fetchedContent, noteData, normalizedTitle }) => {
    const loadedDocument = applyLoadedContent(fetchedContent);
    setSavedAttachments(readNoteAttachments(noteData));
    setSelectedAttachments([]);
    setPendingRemovalBlobNames([]);
    setSubject(normalizedTitle);
    setTitle(normalizedTitle);
    setQueryParamInURL("title", normalizedTitle);
    setCanvasDocumentKey(
      buildCanvasAiDocumentKey({ mode: "online", title: normalizedTitle })
    );
    autoSave.markSaved(
      buildLoadedSnapshot({ loadedDocument, subject: normalizedTitle })
    );
  };

  const viewOfflineNote = async (noteTitle) => {
    setSaveMode("offline");
    let storedNote = await RetrieveNoteByName(noteTitle);
    if (!storedNote) {
      showMessage("error", "Note Not Found");
      return;
    }

    const loadedDocument = applyLoadedContent(storedNote?.content);
    setCanvasDocumentKey(
      buildCanvasAiDocumentKey({ mode: "offline", title: noteTitle })
    );
    clearAttachmentState();
    autoSave.markSaved(
      buildLoadedSnapshot({ loadedDocument, subject: noteTitle })
    );
  };

  const viewOnlineNote = async (noteTitle) => {
    const normalizedTitle = normalizeOnlineTitle(noteTitle);

    try {
      let noteData = await getNote(normalizedTitle);
      const fetchedContent = noteData?.content;
      applyFetchedOnlineNote({ fetchedContent, noteData, normalizedTitle });
      showMessage("success", "Note Fetched");
    } catch (error) {
      showMessage("error", "Note Not Found");
    }
  };

  const viewButtonHandler = async (title = null) => {
    const noteTitle = String(title ?? subject).trim();
    if (!noteTitle) {
      showMessage("error", "Empty Title");
      return;
    }

    setLoading(true);
    try {
      if (noteTitle.startsWith("#")) {
        await viewOfflineNote(noteTitle);
        return;
      }

      await viewOnlineNote(noteTitle);
    } finally {
      setLoading(false);
    }
  };

  const deleteButtonHandler = async () => {
    try {
      if (!String(subject).trim()) {
        showMessage("error", "No Title to delete");
        return;
      }
      setLoading(true);

      setIsLive(false);

      if (subject.startsWith("#")) {
        await DeleteNoteByTitle(subject);
        const offlineNotesTitles = await getAllStoredNoteTitles();
        setRecentNotes(offlineNotesTitles);
      } else {
        await deleteNote(subject);
        const storedRecentTitles =
          JSON.parse(localStorage.getItem(RECENT_TITLES_STORAGE_KEY)) || [];
        const updatedRecentTitles = storedRecentTitles.filter(
          (title) => title !== subject
        );
        localStorage.setItem(
          RECENT_TITLES_STORAGE_KEY,
          JSON.stringify(updatedRecentTitles)
        );
        setRecentNotes(updatedRecentTitles);
        removeQueryParamInURL("title");
      }

      setContent("");
      setCanvasContent(createEmptyCanvasContent());
      setSubject("");
      setTitle("");
      resetCanvasAiDocument();
      clearAttachmentState();
      autoSave.markSaved(
        buildAutoSaveSnapshot({
          canvasContent: createEmptyCanvasContent(),
          content: "",
          subject: "",
        })
      );
      showMessage("success", "Note Deleted");
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const cleanButtonHandler = () => {
    setIsLive(false);
    setContent("");
    setCanvasContent(createEmptyCanvasContent());
    setSubject("");
    setTitle("");
    resetCanvasAiDocument();
    clearAttachmentState();
    autoSave.markSaved(
      buildAutoSaveSnapshot({
        canvasContent: createEmptyCanvasContent(),
        content: "",
        subject: "",
      })
    );
    setSaveMode("online");
    removeQueryParamInURL("title");
  };

  const handleKeyboardEvents = (e) => {
    if (e.key === "Enter") {
      if (isNearbyTransferBetaCode(subject)) {
        e.preventDefault();
        setIsNearbyTransferEnabled(true);
        setSubject("");
        setTitle("");
        removeQueryParamInURL("title");
        showMessage("success", NEARBY_BETA_ENABLED_MESSAGE);
        return;
      }

      viewButtonHandler();
    }
    // Cmd/Ctrl+S bubbles to the window save-shortcut listener; a second
    // handler here would race a duplicate save against it.
  };

  const setQueryParamInURL = (queryKey, queryValue) => {
    const newUrl = new URL(window.location);
    newUrl.searchParams.set(
      String(queryKey).toLocaleLowerCase(),
      String(queryValue).toLocaleLowerCase()
    );
    window.history.pushState({}, "", newUrl);
  };

  const removeQueryParamInURL = (queryKey) => {
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete(String(queryKey).toLocaleLowerCase());
    window.history.pushState({}, "", newUrl);
  };

  const handleOnChangeTitle = (e) => {
    setIsLive(false);

    setSubject(String(e.target.value).toLowerCase());
    setTitle(String(e.target.value).toLowerCase());
    if (e.target.value.startsWith("#")) {
      setSaveMode("offline");
      clearAttachmentState();
    } else {
      setSaveMode("online");
      setQueryParamInURL("title", e.target.value);
    }
  };

  const handleOnChangeContent = (e) => {
    setContent(e);
  };

  const handleOnChangeCanvasContent = (nextContent) => {
    setCanvasContent(nextContent);
  };

  const handleToggleAutoSave = () => {
    const nextAutoSaveState = !isAutoSaveEnabled;
    storeAutoSavePreference(nextAutoSaveState);
    setIsAutoSaveEnabled(nextAutoSaveState);
  };

  const handleEditorModeChange = (nextMode) => {
    setEditorMode(nextMode);
    if (nextMode !== EDITOR_MODES.CANVAS) {
      setRecentDrawerOpen(false);
    }
  };

  const handleToggleCanvasFullscreen = useCallback(() => {
    setIsCanvasFullscreen((previous) => !previous);
  }, []);

  const handleExitCanvasFullscreen = useCallback(() => {
    setIsCanvasFullscreen(false);
  }, []);

  useEffect(() => {
    if (isCanvasMode) return;
    setIsCanvasFullscreen(false);
  }, [isCanvasMode]);

  useEffect(() => {
    if (!isCanvasFullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = LOCKED_BODY_OVERFLOW;
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isCanvasFullscreen]);

  const handleRecentDrawerOpen = () => {
    setRecentDrawerOpen(true);
  };

  const handleRecentDrawerClose = useCallback(() => {
    setRecentDrawerOpen(false);
  }, []);

  const handleRecentNoteTitleClick = (newTitle) => {
    setIsLive(false);
    setContent(null);
    setCanvasContent(createEmptyCanvasContent());
    setSubject(newTitle);
    setTitle(newTitle);
    setRecentDrawerOpen(false);
    viewButtonHandler(newTitle);
  };

  const onToggleViewRecentSwitchHandler = useCallback(async (nextMode) => {
    const mode = nextMode ?? recentViewNoteMode;
    setRecentViewNoteMode(mode);

    if (mode === "offline") {
      let offlineNotesTitles = await getAllStoredNoteTitles();
      setRecentNotes(offlineNotesTitles);
    }

    if (mode === "online") {
      let storedRecentTitles =
        JSON.parse(localStorage.getItem(RECENT_TITLES_STORAGE_KEY)) || [];
      setRecentNotes(storedRecentTitles);
    }
  }, [recentViewNoteMode, setRecentViewNoteMode]);

  const handleInputTitleDropdownChange = (e) => {
    setNoteExpiry(e.target.value);
  };

  const handleFullscreenTitleChange = (nextTitle) => {
    handleOnChangeTitle({ target: { value: nextTitle } });
  };

  const handleFullscreenExpiryChange = (nextExpiry) => {
    setNoteExpiry(nextExpiry);
  };

  const countRemainingAttachmentSlots = () => {
    const keptSavedCount = savedAttachments.filter(
      (attachment) => !pendingRemovalBlobNames.includes(attachment.blobName)
    ).length;
    return MAX_NOTE_ATTACHMENT_COUNT - keptSavedCount - selectedAttachments.length;
  };

  const getAttachmentFileError = (file) => {
    const validation = validateNoteAttachmentFile(file);
    if (!validation.isValid) return validation.message;
    if (file.size > MAX_NOTE_ATTACHMENT_BYTES) return ATTACHMENT_SIZE_ERROR_MESSAGE;

    return null;
  };

  const collectAcceptedAttachments = (pickedFiles) => {
    const selectedFileKeys = new Set(selectedAttachments.map(getAttachmentFileKey));
    let remainingSlots = countRemainingAttachmentSlots();
    const acceptedFiles = [];
    let errorMessage = null;

    for (const file of pickedFiles) {
      const fileError = getAttachmentFileError(file);
      if (fileError) {
        errorMessage = fileError;
        continue;
      }
      if (selectedFileKeys.has(getAttachmentFileKey(file))) continue;
      if (remainingSlots <= 0) {
        errorMessage = ATTACHMENT_COUNT_ERROR_MESSAGE;
        break;
      }
      selectedFileKeys.add(getAttachmentFileKey(file));
      remainingSlots -= 1;
      acceptedFiles.push(file);
    }

    return { acceptedFiles, errorMessage };
  };

  const handleAttachmentsSelect = (files) => {
    const pickedFiles = Array.from(files ?? []);
    if (!pickedFiles.length) return;

    const { acceptedFiles, errorMessage } = collectAcceptedAttachments(pickedFiles);
    if (acceptedFiles.length) {
      setSelectedAttachments((previousFiles) => [...previousFiles, ...acceptedFiles]);
    }
    if (errorMessage) showMessage("error", errorMessage);
  };

  const handleClearSelectedAttachment = (fileKey) => {
    setSelectedAttachments((previousFiles) =>
      previousFiles.filter((file) => getAttachmentFileKey(file) !== fileKey)
    );
  };

  const handleRemoveSavedAttachment = (blobName) => {
    if (!blobName) return;

    setPendingRemovalBlobNames((previousBlobNames) =>
      previousBlobNames.includes(blobName)
        ? previousBlobNames
        : [...previousBlobNames, blobName]
    );
  };

  const handleUndoRemoveSavedAttachment = (blobName) => {
    setPendingRemovalBlobNames((previousBlobNames) =>
      previousBlobNames.filter((pendingBlobName) => pendingBlobName !== blobName)
    );
  };

  const getFreshDownloadAttachment = async (attachment) => {
    if (!isExpiredAttachmentUrl(attachment)) {
      return attachment;
    }

    const noteData = await getNote(normalizeOnlineTitle(subject));
    const refreshedAttachments = readNoteAttachments(noteData);
    setSavedAttachments(refreshedAttachments);
    return refreshedAttachments.find(
      (refreshedAttachment) => refreshedAttachment.blobName === attachment.blobName
    );
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const freshAttachment = await getFreshDownloadAttachment(attachment);
      if (!freshAttachment?.downloadUrl) return;

      await downloadAttachmentFile(freshAttachment);
    } catch (error) {
      showMessage("error", ATTACHMENT_DOWNLOAD_ERROR_MESSAGE);
    }
  };

  const handleOpenReceivedTransfer = ({ title, content, file }) => {
    const receivedTitle = title || "";

    setIsLive(false);
    resetCanvasAiDocument();
    applyLoadedContent(content || "");
    setSubject(receivedTitle);
    setTitle(receivedTitle);
    setSelectedAttachments(file ? [file] : []);
    setSavedAttachments([]);
    setPendingRemovalBlobNames([]);
    showMessage("success", "Local transfer opened");
  };

  if (isSplashVisible) {
    return <SplashScreenAnimation />;
  }

  const pageContentClassName = isCanvasMode
    ? "w-full p-4 md:p-6"
    : "w-full p-4 md:p-10";
  const actionBarShellClassName = isCanvasMode
    ? "relative z-20 w-full"
    : "w-full md:w-[70%]";
  const noteAutoSaveState = {
    ...autoSave,
    isEnabled: isAutoSaveEnabled,
  };

  return (
    <div className="h-full w-full bg-page text-text">
      <div className="h-15 border-b border-border bg-surface shadow-md">
        <Header
          title="Netpad.click"
          padding={"p-[10px_15px]"}
        />
      </div>
      <div className={pageContentClassName}>
        <div className={actionBarShellClassName}>
          <NoteActionBar
            editorMode={editorMode}
            isLoading={loading}
            noteTitle={subject}
            onClear={cleanButtonHandler}
            onDelete={deleteButtonHandler}
            onEditorModeChange={handleEditorModeChange}
            onRecentMenuOpen={handleRecentDrawerOpen}
            onSave={() => saveButtonHandler()}
            onTitleChange={handleOnChangeTitle}
            onTitleDropdownChange={handleInputTitleDropdownChange}
            onTitleKeyDown={handleKeyboardEvents}
            onView={() => viewButtonHandler()}
            saveMode={saveMode}
            titleDropdownOptions={dropdownOptions}
          />
        </div>
        {isNearbyTransferEnabled && !isCanvasMode && (
          <NearbyNetpadsPanel
            className="mt-4 md:hidden"
            hasSavedAttachment={savedAttachments.length > 0}
            hasSelectedFile={selectedAttachments.length > 0}
            onOpenReceivedTransfer={handleOpenReceivedTransfer}
            session={nearbyTransferSession}
          />
        )}
        {isCanvasMode ? (
          <div
            className="mt-5 motion-safe:animate-workspace-in"
            key="workspace-canvas"
          >
            <CanvasWorkspace
              documentKey={canvasDocumentKey}
              isCloudSyncEnabled={isCanvasCloudSyncEnabled}
              isFullscreen={isCanvasFullscreen}
              onChange={handleOnChangeCanvasContent}
              onImageSyncError={(syncErrorMessage) =>
                showMessage("error", syncErrorMessage)
              }
              onSaveRequest={() => saveButtonHandler()}
              onToggleFullscreen={handleToggleCanvasFullscreen}
              ref={canvasWorkspaceRef}
              value={canvasContent}
            />
            {isCanvasFullscreen && (
              <CanvasFullscreenBar
                expiry={noteExpiry}
                expiryOptions={dropdownOptions}
                isSaving={autoSave.isSaving || loading}
                onClear={cleanButtonHandler}
                onDelete={deleteButtonHandler}
                onExitFullscreen={handleExitCanvasFullscreen}
                onExpiryChange={handleFullscreenExpiryChange}
                onSave={() => saveButtonHandler()}
                onTitleChange={handleFullscreenTitleChange}
                onView={() => viewButtonHandler()}
                title={subject}
              />
            )}
          </div>
        ) : (
          <div
            className="mt-5 block font-mono motion-safe:animate-workspace-in md:flex md:place-items-start md:gap-[15px]"
            key="workspace-note"
          >
            <NoteEditorWorkspace
              autoSave={noteAutoSaveState}
              content={content}
              fileTransferPromo={fileTransferPromo}
              isAttachmentHighlighted={isFileTransferAttachmentHighlighted}
              onAttachmentDownload={handleDownloadAttachment}
              onAttachmentRemovalUndo={handleUndoRemoveSavedAttachment}
              onAttachmentRemove={handleRemoveSavedAttachment}
              onAttachmentSelect={handleAttachmentsSelect}
              onAttachmentSelectionClear={handleClearSelectedAttachment}
              onContentChange={handleOnChangeContent}
              onToggleAutoSave={handleToggleAutoSave}
              pendingRemovalBlobNames={pendingRemovalBlobNames}
              saveMode={saveMode}
              savedAttachments={savedAttachments}
              selectedAttachments={selectedAttachments}
            />
            <aside className="hidden flex-col gap-4 md:flex md:w-[24%]">
              <RecentTitlesPanel
                onModeChange={onToggleViewRecentSwitchHandler}
                onTitleSelect={handleRecentNoteTitleClick}
                selectedMode={recentViewNoteMode}
                titles={recentNotes}
              />
              {isNearbyTransferEnabled && (
                <NearbyNetpadsPanel
                  hasSavedAttachment={savedAttachments.length > 0}
                  hasSelectedFile={selectedAttachments.length > 0}
                  onOpenReceivedTransfer={handleOpenReceivedTransfer}
                  session={nearbyTransferSession}
                />
              )}
            </aside>
          </div>
        )}
        <RecentTitlesDrawer
          isOpen={isCanvasMode && isRecentDrawerOpen}
          onClose={handleRecentDrawerClose}
          onModeChange={onToggleViewRecentSwitchHandler}
          onTitleSelect={handleRecentNoteTitleClick}
          selectedMode={recentViewNoteMode}
          titles={recentNotes}
        />

        <div className="font-montserrat z-50 fixed bottom-0 left-1/2 transform -translate-x-1/2 mb-10">
          <InfoMessageComponent
            typeOfMessage={typeOfMessage}
            message={message}
          />
        </div>
        {!isCanvasMode && (
          <footer className="relative z-10 mt-6 border-t border-border bg-surface font-sen md:fixed md:bottom-0 md:inset-x-0 md:mt-0">
            <div className="flex items-center justify-center p-1">
              <p className="text-text-muted text-md">
                © {COPYRIGHT_START_YEAR}-{new Date().getFullYear()}{" "}
                <mark className="bg-inherit text-primary">Netpad.click.</mark>{" "}
                All rights reserved.{" "}
                <a
                  href="mailto:communications@netpad.click"
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary underline"
                >
                  Mail
                </a>
              </p>
            </div>
          </footer>
        )}
      </div>
    </div>
  );
};

export default HomeScreen;
